/**
 * User types and what each can reach — the ONE place this is defined.
 *
 * Menus, routes and pages ask `canAccess(who, feature)`; they never check
 * permission strings themselves. To give a feature to another user type, add it
 * to that type's list in FEATURES_BY_TYPE below. Nothing else changes.
 */

import { ROSTER } from "@/Apps/Agents/roster";

export type UserType = "ambidStaff" | "agency" | "agentsClient" | "platform";

export type Feature =
  | "settings"
  | "profile"
  | "domains"
  | "integrations"
  | "connectClaude"
  | "team"
  | "clientAccess"
  | "support"
  | "docs";

/** Who is asking: the login email and the permission strings from the session. */
export interface Who {
  email?: string | null;
  permissions?: string[];
}

/** Email domains that count as Ambid staff (the roster covers team members with other addresses). */
export const AMBID_EMAIL_DOMAINS = ["ambidtech.com"];

const BASE: Feature[] = ["settings", "profile", "domains", "integrations", "docs"];

/**
 * To open Team / Client Access to agencies later, add "team" and "clientAccess"
 * to the `agency` list. That is the whole change.
 */
export const FEATURES_BY_TYPE: Record<UserType, Feature[]> = {
  ambidStaff: [...BASE, "connectClaude", "team", "clientAccess", "support"], // Connect Claude is Ambid-team only for now
  agency: [...BASE, "support"],
  agentsClient: [...BASE, "team", "clientAccess"],
  platform: [...BASE, "support"],
};

/** These pages live inside the Agents app, so the `agents` permission is also required. */
const NEEDS_AGENTS_PERMISSION: Feature[] = ["team", "clientAccess"];

/** The Ambid team: anyone on the Agents roster (the one team list), or any @ambidtech.com address. */
export function isAmbidStaff(email?: string | null): boolean {
  const e = (email || "").trim().toLowerCase();
  const at = e.split("@");
  if (at.length === 2 && AMBID_EMAIL_DOMAINS.includes(at[1])) return true;
  return ROSTER.some((m) => (m.email || "").toLowerCase() === e);
}

export function getUserType({ email, permissions = [] }: Who): UserType {
  if (isAmbidStaff(email)) return "ambidStaff";
  if (permissions.includes("agency")) return "agency";
  if (permissions.includes("agents")) return "agentsClient";
  return "platform";
}

export function canAccess(who: Who, feature: Feature): boolean {
  const permissions = who.permissions || [];
  const type = getUserType(who);
  if (!FEATURES_BY_TYPE[type].includes(feature)) return false;
  if (NEEDS_AGENTS_PERMISSION.includes(feature) && !permissions.includes("agents")) return false;
  // Platform users only had Integrations with the launchOS permission; keep that.
  if (feature === "integrations" && type === "platform" && !permissions.includes("launchOS")) return false;
  return true;
}

/**
 * Rollout stage, changed in code and shipped with a deploy:
 *  "staff" — new shell for Ambid staff only (where we started)
 *  "all"   — new shell for every user (live since Oct 30; Connect Claude, Team and Clients stay limited by FEATURES_BY_TYPE)
 *  "off"   — everyone back on the old menus
 */
export const ROLLOUT_MODE: "staff" | "all" | "off" = "all";

/**
 * Rollout switch. Off restores the old menus exactly.
 *  - override "off" / "on": a per-browser setting (localStorage `unifiedShell`), for testing and instant rollback
 *  - mode "off" / "all": the ROLLOUT_MODE stage above
 *  - otherwise: on for Ambid staff only
 */
export function isUnifiedShellOn(opts: {
  email?: string | null;
  override?: string | null;
  mode?: string | null;
}): boolean {
  if (opts.override === "off") return false;
  if (opts.override === "on") return true;
  if (opts.mode === "off") return false;
  if (opts.mode === "all") return true;
  return isAmbidStaff(opts.email);
}

/** The tabs on the one Settings page, in order, each tied to the feature that unlocks it. */
export type SettingsTab = "profile" | "team" | "clients" | "domains" | "connections" | "claude";
const SETTINGS_TABS: [SettingsTab, Feature][] = [
  ["profile", "profile"],
  ["team", "team"],
  ["clients", "clientAccess"],
  ["domains", "domains"],
  ["connections", "integrations"],
  ["claude", "connectClaude"],
];

export function settingsTabsFor(who: Who): SettingsTab[] {
  return SETTINGS_TABS.filter(([, feature]) => canAccess(who, feature)).map(([tab]) => tab);
}

/** Agents users have Settings inside the Agents app; everyone else at /settings. Same page, same tabs. */
export function settingsPath(who: Who, tab?: SettingsTab): string {
  const base = (who.permissions || []).includes("agents") ? "/agents/settings" : "/settings";
  return tab ? `${base}?tab=${tab}` : base;
}

/** Old addresses that now open a Settings tab. */
export const LEGACY_TAB_PATHS: Record<string, SettingsTab> = {
  "/profile": "profile",
  "/domains": "domains",
  "/integrations": "connections",
  "/connect-claude": "claude",
};

export function docsUrl(who: Who): string {
  return (who.permissions || []).includes("agents") ? "https://docs.launchos.com/docs/agents" : "https://docs.launchos.com";
}
